import { Paper, styled, Table, TableBody, TableCell, TableContainer, TableHead, TableRow } from "@mui/material";
import { theme } from '../../provider/ThemeProvider';
import { nl2br } from "../../utils/nl2br";

type TableProps = {
  rows: {title:string, value:string | React.ReactNode | null |JSX.Element}[];
  hover?:boolean;
  width?: string | number;
};

export const HeadlessTableForPDF = (props: TableProps) => {
  const { rows, hover, width } = props;
  return (
    <Table>
      <TableBody style={{border: "1px solid  rgba(224, 224, 224, 1)"}}>
        {rows.map((row, index) => {
          return (
            <TableRow hover={hover} role='checkbox' tabIndex={-1} key={index}>
              <CustomizedTitleForPDF width={width}>{row.title}</CustomizedTitleForPDF>
              <CustomizedTableCellForPDF>{nl2br(row.value)}</CustomizedTableCellForPDF>
            </TableRow>
          );
        })}
      </TableBody>
    </Table>
  );
};

interface MultiColmunsTablePropsA {
  headers: string[];
  rows: {value: string, align: "center" | "inherit" | "left" | "right" | "justify" | undefined}[][];
  indexColors?: string[];
  isAllCellCenter?: boolean;
  isWhiteIndexFont?: boolean;
  isFirstCellColor?: boolean;
  width?: string;
  printHeaderWidthAuto?:boolean;
}

/**
 * 複数列のテーブルを作成する（PDF用）
 * @param headers ヘッダー　配列の先頭は空白
 * @param rows 行　配列の先頭はタイトル
 * @param indexColors 行タイトルの背景色
 * @param isAllCellCenter セルを全て中央寄せにするか
 * @returns 
 */
export const MultiColmunsTableAForPDF: React.FC<MultiColmunsTablePropsA> = ({
  headers,
  rows,
  indexColors,
  isAllCellCenter,
  isWhiteIndexFont,
  width, isFirstCellColor,
  printHeaderWidthAuto,
}) => {

  return (
    <StyledTableContainerForPDF sx={width ? {width: 'fit-content'} : {}}>
      <CustomizedTableForPDF aria-label="custom table" sx={width ? {width : width} : {}}>
        <TableHead>
          <TableRow>
            {headers.map((header, index) => (
              index === 0  && !isFirstCellColor ? 
              (
                <CustomizedTableFirstCellForPDF align="center">{nl2br(header)}</CustomizedTableFirstCellForPDF>
              )
              : 
              (
                <CustomizedTableHeadCellForPDF align="center">{nl2br(header)}</CustomizedTableHeadCellForPDF>
              )
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((row, index) => (
            <TableRow key={index}>
              {row.map((row, cellIndex) => (
                cellIndex === 0 ? (
                  <CustomizedTableHeadColmunCellForPDF
                    component="th"
                    scope="row"
                    color={indexColors && indexColors[index] ? indexColors[index] : undefined}
                    fontColor={isWhiteIndexFont ? 'white' : theme.palette.text.primary}
                    printWidthAuto={printHeaderWidthAuto}
                  >
                    {nl2br(row.value)}
                  </CustomizedTableHeadColmunCellForPDF>
                )
                : (
                  <StyledTableCellForPDF align={isAllCellCenter ? 'center' : row.align}>{row?.value}</StyledTableCellForPDF>
                )
              ))}
            </TableRow>
          ))}
        </TableBody>
      </CustomizedTableForPDF>
    </StyledTableContainerForPDF>
  );
};


interface MultiColmunsTableProps {
  headers: string[];
  rows: {value: string, align: "center" | "inherit" | "left" | "right" | "justify" | undefined}[][];
  indexColors?: string[];
  isAllCellCenter?: boolean;
  isWhiteIndexFont?: boolean;
  isFirstCellColor?: boolean;
  width?: string;
  printHeaderWidthAuto?:boolean;
  smallFont?: boolean;
}

/**
 * 複数列のテーブルを作成する（PDF用）
 * @param headers ヘッダー　配列の先頭は空白
 * @param rows 行　配列の先頭はタイトル
 * @param indexColors 行タイトルの背景色
 * @param isAllCellCenter セルを全て中央寄せにするか
 * @param isFirstCellColor 最初のセルの色付け
 * @returns 
 */
export const MultiColmunsTableForPDF: React.FC<MultiColmunsTableProps> = ({
  headers,
  rows,
  indexColors,
  isAllCellCenter,
  isWhiteIndexFont,
  width, isFirstCellColor,
  printHeaderWidthAuto,
  smallFont
}) => {

  return (
    <StyledTableContainerForPDF sx={width ? {width: 'fit-content'} : {}}>
      <CustomizedTableForPDF aria-label="custom table" sx={width ? {width : width} : {}}>
        <TableHead>
          <TableRow>
            {headers.map((header, index) => (
              index === 0  && !isFirstCellColor? 
              (
                <CustomizedTableFirstCellForPDF  align="center" smallFont={smallFont}>{nl2br(header)}</CustomizedTableFirstCellForPDF>
              )
              : 
              (
                <CustomizedTableHeadCellForPDF align="center">{nl2br(header)}</CustomizedTableHeadCellForPDF>
              )
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((row, index) => (
            <TableRow key={index}>
              {row.map((r, cellIndex) => (
                cellIndex === 0 ? (
                  <CustomizedTableHeadColmunCellForPDF
                    component="th"
                    scope="row"
                    color={indexColors && indexColors[index] ? indexColors[index] : undefined}
                    fontColor={isWhiteIndexFont ? 'white' : theme.palette.text.primary}
                    printWidthAuto={printHeaderWidthAuto}
                  >
                    {nl2br(r.value)}
                  </CustomizedTableHeadColmunCellForPDF>
                )
                : (
                  <StyledTableCellForPDF align={isAllCellCenter ? 'center' : r.align}>{nl2br(r.value)}</StyledTableCellForPDF>
                )
              ))}
            </TableRow>
          ))}
        </TableBody>
      </CustomizedTableForPDF>
    </StyledTableContainerForPDF>
  );
};

interface MultiColspanColmunsTable {
  headers: {value: string, colspan: number}[];
  rows: {value?: string | number, colspan: number}[][];
  indexColors?: string[];
  isAllCellCenter?: boolean;
  isWhiteIndexFont?: boolean;
  isFirstCellColor?: boolean;
  width?: string;
}


export const MultiColspanColmunsTableForPDF: React.FC<MultiColspanColmunsTable> = ({
  headers,
  rows,
  indexColors,
  isAllCellCenter,
  isWhiteIndexFont,
  width, isFirstCellColor
}) => {

  return (
    <TableContainer component={Paper} sx={width ? {width: 'fit-content'} : {}} style={{width: width, boxShadow: 'none'}}>
      <CustomizedTableForPDF aria-label="custom table" sx={width ? {width : width} : {}}>
        <TableHead>
          <TableRow>
            {headers.map((header, index) => (
              index === 0  && !isFirstCellColor? 
              (
                <TableCell colSpan={header.colspan} style={{padding: '0px 2px'}}>{header.value}</TableCell>
              )
              : 
              (
                <CustomizedTableHeadCellForPDF align="center" colSpan={header.colspan} style={{padding: '0px 4px'}}>{header.value}</CustomizedTableHeadCellForPDF>
              )
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((row, index) => (
            <TableRow key={index}>
              {row.map((cell, cellIndex) => (
                cellIndex === 0 ? (
                  <CustomizedTableHeadColmunCellForPDF
                    component="th"
                    scope="row"
                    colSpan={cell.colspan}
                    color={indexColors && indexColors[index] ? indexColors[index] : undefined}
                    fontColor={isWhiteIndexFont ? 'white' : theme.palette.text.primary}
                    style={{padding: '8px 8px', width: 200}}
                  >
                    {cell.value}
                  </CustomizedTableHeadColmunCellForPDF>
                )
                : (
                  <TableCell align={isAllCellCenter ? 'center' : 'left'}  colSpan={cell.colspan} style={{padding: '0', minWidth: 30}}> {cell.value}</TableCell>
                )
              ))}
            </TableRow>
          ))}
        </TableBody>
      </CustomizedTableForPDF>
    </TableContainer>
  );
};

type CustomizedTitleProps = {
  width?: string | number;
}

const CustomizedTitleForPDF = styled(TableCell)<CustomizedTitleProps>(({ width = '65%' }) => ({
  fontSize: '12px',
  marginLeft: '8px',
  background: "#F2F2F2",
  width: width,
  fontFamily: 'ABeeZee',
}));


const CustomizedTableCellForPDF = styled(TableCell)({
  padding: '12px 16px',
  wordBreak: "break-all",
  fontFamily: 'ABeeZee',
  fontSize: '12px',
});

const CustomizedTableForPDF = styled(Table)({
  minWidth: 500,
  '& th': {
    border: '1px solid rgba(224, 224, 224, 1)',
    fontWeight: 'bold',
  },
  '& td': {
    border: '1px solid rgba(224, 224, 224, 1)',
    whiteSpace: 'pre-wrap'
  },
  '& tr:first-child th': {
    maxWidth: '15rem',
    minWidth: '4rem',
  },
  '& tr:last-child td': {
  },
  '& tr td:first-child': {
    maxWidth: '15rem',
    minWidth: '4rem',
  },
  '& tr td:last-child': {
  },
});

type CustomizedTableFirstCellProps = {
  smallFont?: boolean;
}

const CustomizedTableFirstCellForPDF = styled(TableCell)<CustomizedTableFirstCellProps>`
  padding: 4px;
  font-size:10.5px;
`;

const CustomizedTableHeadCellForPDF = styled(TableCell)({
  background: "#E7F3FB",
  padding:'4px',
  fontSize: '10.5px',
});

const CustomizedTableHeadColmunCellForPDF = styled(TableCell).bind({})<{ color?: string, fontColor: string, printWidthAuto?:boolean; }>`
  background: ${({ color }) => color ? color : "#F7F5F5"};
  color: ${({ fontColor }) => fontColor};
  max-width: 15rem;
  min-width: 3rem;
  padding: 4px 2px;
  width: ${({printWidthAuto})=> printWidthAuto ? 'auto': '30%'};
  font-size: 10px;
  letter-spacing: 0px;
`;

const StyledTableCellForPDF = styled(TableCell)`
  max-width: 30%;
  font-size: 10px;
  letter-spacing: 0px;
  padding: 4px 2px;
`

const StyledTableContainerForPDF = styled(TableContainer)`
    .MuiTable-root {
      min-width: auto;
    }
`
